import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { RequestYourCity } from "../features/cityRequest/RequestYourCity";

export function LandingRoute() {
  const { t } = useTranslation();
  return (
    <div className="min-h-0 flex-1 bg-bg text-fg">
      <Hero />
      <HowItWorks />
      <WhoBenefits />
      <WhyBuilt />
      <RequestYourCity />
      <CtaFooter />
    </div>
  );

  function Hero() {
    return (
      <section className="relative overflow-hidden px-5 py-20 sm:px-10 sm:py-28">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-volt/15 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-8rem] right-[-8rem] h-[32rem] w-[32rem] rounded-full bg-ember/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-5xl">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-line-strong bg-surface/60 px-3 py-1.5 backdrop-blur">
            <span className="live-dot" aria-hidden="true" />
            {t("landing.eyebrow")}
          </span>
          <h1 className="display reveal mt-10 text-5xl sm:text-7xl md:text-[6rem]">
            {t("landing.hero_title")}
          </h1>
          <p className="reveal reveal-delay-1 mt-8 max-w-2xl text-lg leading-relaxed text-fg-mute sm:text-xl">
            {t("landing.hero_sub")}
          </p>
          <div className="reveal reveal-delay-2 mt-12 flex flex-wrap items-center gap-5">
            <Link
              to="/"
              className="btn-volt group inline-flex items-center gap-3 px-6 py-3.5 text-sm tracking-tight"
            >
              {t("landing.cta_view_chargers")}
              <span
                aria-hidden="true"
                className="transition-transform duration-300 ease-out group-hover:translate-x-0.5"
              >
                →
              </span>
            </Link>
            <Link
              to="/how-to"
              className="text-sm font-medium tracking-tight text-fg-mute underline decoration-volt decoration-[2px] underline-offset-[6px] hover:text-fg"
            >
              {t("landing.cta_how_it_works")}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  function HowItWorks() {
    const steps = [
      { n: "01", k: "find" },
      { n: "02", k: "check" },
      { n: "03", k: "report" },
    ] as const;
    return (
      <section className="hairline border-t px-5 py-20 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-5xl">
          <p className="eyebrow text-volt">{t("landing.how_eyebrow")}</p>
          <h2 className="display mt-4 text-4xl sm:text-5xl">
            {t("landing.how_title")}
          </h2>
          <ol className="mt-14 grid gap-px overflow-hidden rounded-lg border border-line bg-line md:grid-cols-3">
            {steps.map((s) => (
              <li
                key={s.n}
                className="group relative bg-surface p-8 transition-colors duration-300 hover:bg-surface-raised"
              >
                <span
                  aria-hidden="true"
                  className="num text-xs tracking-[0.18em] text-fg-faint"
                >
                  {s.n}
                </span>
                <h3 className="mt-6 text-lg font-medium tracking-tight text-fg">
                  {t(`landing.how_steps.${s.k}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-fg-mute">
                  {t(`landing.how_steps.${s.k}.body`)}
                </p>
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 h-px w-0 bg-volt transition-all duration-500 ease-out group-hover:w-full"
                />
              </li>
            ))}
          </ol>
        </div>
      </section>
    );
  }

  function WhoBenefits() {
    const blocks = [
      { k: "drivers", glyph: "◆" },
      { k: "stations", glyph: "◉" },
      { k: "communities", glyph: "◈" },
    ] as const;
    return (
      <section className="hairline border-t px-5 py-20 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-5xl">
          <p className="eyebrow text-volt">{t("landing.who_eyebrow")}</p>
          <h2 className="display mt-4 text-4xl sm:text-5xl">
            {t("landing.who_title")}
          </h2>
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {blocks.map((b) => (
              <article
                key={b.k}
                className="surface-cinematic group p-7 transition-transform duration-500 ease-out hover:-translate-y-1"
              >
                <span
                  aria-hidden="true"
                  className="num text-2xl text-volt"
                >
                  {b.glyph}
                </span>
                <h3 className="mt-6 text-xl font-medium tracking-tight text-fg">
                  {t(`landing.who_blocks.${b.k}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-fg-mute">
                  {t(`landing.who_blocks.${b.k}.body`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function WhyBuilt() {
    return (
      <section className="surface-mesh relative mx-5 my-12 overflow-hidden px-8 py-20 sm:mx-10 sm:px-16 sm:py-28">
        <div className="relative mx-auto grid max-w-5xl gap-10 md:grid-cols-[1fr_2fr]">
          <div>
            <p className="eyebrow text-volt">{t("landing.why_eyebrow")}</p>
            <h2 className="display mt-4 text-4xl sm:text-5xl">
              {t("landing.why_title")}
            </h2>
          </div>
          <div className="space-y-5 text-lg leading-relaxed text-fg-mute">
            <p>{t("landing.why_body_1")}</p>
            <p>{t("landing.why_body_2")}</p>
          </div>
        </div>
      </section>
    );
  }

  function CtaFooter() {
    return (
      <section className="hairline relative overflow-hidden border-t px-5 py-24 sm:px-10 sm:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-32 mx-auto h-64 max-w-2xl rounded-full bg-volt/10 blur-3xl"
        />
        <div className="relative mx-auto flex max-w-5xl flex-col items-start gap-8">
          <p className="eyebrow text-volt">{t("landing.final_eyebrow")}</p>
          <h2 className="display max-w-3xl text-4xl sm:text-6xl">
            {t("landing.final_title")}
          </h2>
          <Link
            to="/"
            className="btn-volt group inline-flex items-center gap-3 px-7 py-4 text-base tracking-tight"
          >
            {t("landing.cta_view_chargers")}
            <span
              aria-hidden="true"
              className="transition-transform duration-300 ease-out group-hover:translate-x-0.5"
            >
              →
            </span>
          </Link>
        </div>
      </section>
    );
  }
}
